import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiTrash2, FiPlus, FiMinus, FiPackage, FiHeart, FiShoppingCart, FiTruck } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { toast } from 'react-toastify';
import './CartPage.css';

const TABS = [
  { key: 'cart', label: 'Cart', icon: <FiShoppingCart size={16}/> },
  { key: 'wishlist', label: 'Wishlist', icon: <FiHeart size={16}/> },
  { key: 'current', label: 'Current Orders', icon: <FiTruck size={16}/> },
  { key: 'previous', label: 'Previous Orders', icon: <FiPackage size={16}/> },
];

const STATUS_COLORS = {
  PENDING_CONFIRMATION: '#E65100', PLACED: '#C9A84C', CONFIRMED: '#6A1B9A', PACKED: '#C2185B',
  SHIPPED: '#722F37', OUT_FOR_DELIVERY: '#800020', DELIVERED: '#2E7D32',
  CANCELLED: '#B71C1C',
};

export default function CartPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { removeFromCart, fetchCounts } = useApp();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'cart');
  const [data, setData] = useState({ cart: [], wishlist: [], current: [], previous: [] });
  const [loading, setLoading] = useState(false);
  const [showMore, setShowMore] = useState({ cart: 5, wishlist: 5, current: 5, previous: 5 });
  const [paymentSession, setPaymentSession] = useState(null);
  const [paymentStep, setPaymentStep] = useState('idle'); // idle | address | choose | qr | placing | queued | timeout | error
  const [qrCountdown, setQrCountdown] = useState(300); // 5 minutes in seconds
  const [paymentError, setPaymentError] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [newAddress, setNewAddress] = useState({ label: '', address: '', pincode: '' });
  const [offerInput, setOfferInput] = useState('');
  const [offerMessage, setOfferMessage] = useState({ text: '', kind: '' });
  const [applyingOffer, setApplyingOffer] = useState(false);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t) setActiveTab(t);
  }, [searchParams]);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [cartRes, wishRes, ordersRes] = await Promise.all([
        axios.get('/api/cart?flag=C'),
        axios.get('/api/cart?flag=W'),
        axios.get('/api/orders'),
      ]);
      const orders = ordersRes.data.orders || [];
      setData({
        cart: cartRes.data.items || [],
        wishlist: wishRes.data.items || [],
        current: orders.filter(o => !['DELIVERED','CANCELLED'].includes(o.status)),
        previous: orders.filter(o => ['DELIVERED','CANCELLED'].includes(o.status)),
      });
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRemove = async (productId, flag) => {
    await removeFromCart(productId, flag);
    fetchAll(); fetchCounts();
  };

  const handleQtyChange = async (productId, qty) => {
    if (qty < 1) return;
    await axios.put('/api/cart', { productId, quantity: qty, flag: 'C' });
    fetchAll();
  };

  const handleCheckout = async () => {
    if (!data.cart.length) return;
    try {
      // Fetch addresses first
      const addrRes = await axios.get('/api/auth/addresses');
      const addrs = addrRes.data.addresses || [];
      setAddresses(addrs);
      setSelectedAddress(addrs.length > 0 ? addrs[0] : null);
      setPaymentStep('address');
      setPaymentError('');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Checkout failed');
    }
  };

  const handleAddressConfirmed = async () => {
    if (!selectedAddress) {
      toast.error('Please select or add a delivery address');
      return;
    }
    try {
      const res = await axios.post('/api/orders/checkout');
      setPaymentSession({ ...res.data, deliveryAddress: selectedAddress });
      setOfferInput('');
      setOfferMessage({ text: '', kind: '' });
      setPaymentStep('choose');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Checkout failed');
    }
  };

  // Re-prices the checkout on the server. An invalid code keeps the current session.
  const applyOffer = async (code) => {
    setApplyingOffer(true);
    try {
      const res = await axios.post('/api/orders/checkout', code ? { offerCode: code } : {});
      if (res.data.offerError) {
        setOfferMessage({ text: res.data.offerError, kind: 'error' });
        return;
      }
      setPaymentSession({ ...res.data, deliveryAddress: paymentSession?.deliveryAddress });
      setOfferMessage(
        res.data.offer
          ? { text: `${res.data.offer.code} applied: ${res.data.offer.summary}. You save ₹${Number(res.data.offer.discount).toLocaleString('en-IN')}`, kind: 'success' }
          : { text: '', kind: '' }
      );
      if (!code) setOfferInput('');
    } catch (e) {
      setOfferMessage({ text: e.response?.data?.message || 'Could not apply the offer', kind: 'error' });
    } finally {
      setApplyingOffer(false);
    }
  };

  const handleAddNewAddress = async () => {
    if (!newAddress.address || !newAddress.pincode) {
      toast.error('Address and pincode are required');
      return;
    }
    try {
      const res = await axios.post('/api/auth/addresses', newAddress);
      const addrs = res.data.addresses || [];
      setAddresses(addrs);
      setSelectedAddress(addrs[addrs.length - 1]);
      setNewAddress({ label: '', address: '', pincode: '' });
      toast.success('Address added!');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to add address');
    }
  };

  const handlePayByQR = () => {
    setPaymentStep('qr');
    setQrCountdown(300);
  };

  const handlePaid = async () => {
    if (!paymentSession?.orderId) return;
    setPaymentStep('placing');
    try {
      await axios.post('/api/orders/place', {
        orderId: paymentSession.orderId,
        paymentMode: 'qr',
        address: paymentSession.deliveryAddress,
        offerCode: paymentSession.offer?.code,
      });
      setPaymentStep('queued');
      toast.success('Order queued for payment confirmation!');
      fetchAll();
      fetchCounts();
      setTimeout(() => { setPaymentStep('idle'); setActiveTab('current'); }, 3000);
    } catch (e) {
      setPaymentError(e.response?.data?.message || 'Failed to place order');
      setPaymentStep('error');
    }
  };

  const handlePayOffline = async () => {
    if (!paymentSession?.orderId) return;
    setPaymentStep('placing');
    try {
      await axios.post('/api/orders/place', {
        orderId: paymentSession.orderId,
        paymentMode: 'offline',
        address: paymentSession.deliveryAddress,
        offerCode: paymentSession.offer?.code,
      });
      setPaymentStep('queued');
      toast.success('Order queued for payment confirmation!');
      fetchAll();
      fetchCounts();
      setTimeout(() => { setPaymentStep('idle'); setActiveTab('current'); }, 3000);
    } catch (e) {
      setPaymentError(e.response?.data?.message || 'Failed to place order');
      setPaymentStep('error');
    }
  };

  // QR countdown timer
  useEffect(() => {
    if (paymentStep !== 'qr') return;
    if (qrCountdown <= 0) {
      setPaymentStep('timeout');
      return;
    }
    const timer = window.setTimeout(() => setQrCountdown(c => c - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [paymentStep, qrCountdown]);

  const cartTotal = data.cart.reduce((s, i) => s + (i.selling_price * i.quantity), 0);
  const cartOrig = data.cart.reduce((s, i) => s + ((i.origprice || i.selling_price) * i.quantity), 0);
  const savings = cartOrig - cartTotal;

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (paymentStep !== 'idle') {
    return (
      <div className="page-container demo-payment-screen">
        <div className="demo-payment-card">
          {/* Address selection */}
          {paymentStep === 'address' && (
            <>
              <h2>Select Delivery Address</h2>
              {addresses.length > 0 ? (
                <div className="address-list">
                  {addresses.map((addr) => (
                    <label key={addr.id} className={`address-option${selectedAddress?.id === addr.id ? ' selected' : ''}`}>
                      <input
                        type="radio"
                        name="delivery-address"
                        checked={selectedAddress?.id === addr.id}
                        onChange={() => setSelectedAddress(addr)}
                      />
                      <div>
                        <strong>{addr.label || 'Address'}</strong>
                        <p>{addr.address}, {addr.pincode}</p>
                      </div>
                    </label>
                  ))}
                </div>
              ) : (
                <p>No saved addresses. Add one below to continue.</p>
              )}
              <div className="new-address-form">
                <h4>Add New Address</h4>
                <input placeholder="Label (e.g. Home, Office)" value={newAddress.label} onChange={e => setNewAddress(a => ({...a, label: e.target.value}))} />
                <input placeholder="Full address" value={newAddress.address} onChange={e => setNewAddress(a => ({...a, address: e.target.value}))} />
                <input placeholder="Pincode" value={newAddress.pincode} onChange={e => setNewAddress(a => ({...a, pincode: e.target.value}))} />
                <button className="btn-outline" onClick={handleAddNewAddress}>Add Address</button>
              </div>
              <button className="btn-primary" onClick={handleAddressConfirmed} disabled={!selectedAddress}>
                Continue to Payment
              </button>
              <button className="btn-link" onClick={() => setPaymentStep('idle')}>Cancel</button>
            </>
          )}

          {/* Choose payment method */}
          {paymentStep === 'choose' && (
            <>
              <h2>Choose Payment Method</h2>
              <div className="demo-payment-meta">
                <span>Order: {paymentSession?.orderId}</span>
              </div>

              <div className="offer-box">
                <label htmlFor="offer-code">Have an offer code?</label>
                {paymentSession?.offer ? (
                  <div className="offer-applied">
                    <span className="offer-chip">{paymentSession.offer.code}</span>
                    <button className="btn-link" onClick={() => applyOffer('')} disabled={applyingOffer}>Remove</button>
                  </div>
                ) : (
                  <div className="offer-row">
                    <input
                      id="offer-code"
                      value={offerInput}
                      onChange={e => { setOfferInput(e.target.value.toUpperCase()); setOfferMessage({ text: '', kind: '' }); }}
                      onKeyDown={e => { if (e.key === 'Enter' && offerInput.trim()) applyOffer(offerInput.trim()); }}
                      placeholder="Enter code"
                      autoComplete="off"
                    />
                    <button className="btn-outline" onClick={() => applyOffer(offerInput.trim())} disabled={!offerInput.trim() || applyingOffer}>
                      {applyingOffer ? 'Applying…' : 'Apply'}
                    </button>
                  </div>
                )}
                {offerMessage.text && (
                  <p className={`offer-message ${offerMessage.kind}`} role={offerMessage.kind === 'error' ? 'alert' : 'status'}>{offerMessage.text}</p>
                )}
              </div>

              <div className="payment-summary">
                <div className="summary-row"><span>Subtotal</span><span>₹{Number(paymentSession?.subtotal || 0).toLocaleString('en-IN')}</span></div>
                {Number(paymentSession?.discount) > 0 && (
                  <div className="summary-row savings"><span>Offer discount</span><span>−₹{Number(paymentSession.discount).toLocaleString('en-IN')}</span></div>
                )}
                <div className="summary-row"><span>Shipping</span><span>{Number(paymentSession?.shipping) > 0 ? `₹${Number(paymentSession.shipping).toLocaleString('en-IN')}` : 'FREE'}</span></div>
                <div className="summary-row total"><span>Total</span><span>₹{Number(paymentSession?.total || 0).toLocaleString('en-IN')}</span></div>
              </div>

              <div className="payment-options">
                <button className="btn-primary payment-option-btn" onClick={handlePayByQR}>
                  Pay by QR (UPI)
                </button>
                <button className="btn-outline payment-option-btn" onClick={handlePayOffline}>
                  Pay Offline
                </button>
              </div>
              <button className="btn-link" onClick={() => setPaymentStep('idle')}>Cancel and return to cart</button>
            </>
          )}

          {/* QR code with 5 min timer */}
          {paymentStep === 'qr' && (
            <>
              <h2>Scan & Pay</h2>
              <p>Pay ₹{Number(paymentSession?.total || 0).toLocaleString('en-IN')} to <strong>{paymentSession?.upiId}</strong></p>
              <img src={paymentSession?.qrUrl} alt="UPI QR Code" className="demo-qr" />
              <div className="demo-payment-meta">
                <span>Order: {paymentSession?.orderId}</span>
              </div>
              <div className="demo-payment-timer">
                Time remaining: <strong>{formatTime(qrCountdown)}</strong>
              </div>
              <button className="btn-primary" onClick={handlePaid}>
                I've Paid
              </button>
              <button className="btn-link" onClick={() => setPaymentStep('choose')}>Back to payment options</button>
            </>
          )}

          {/* Placing order */}
          {paymentStep === 'placing' && (
            <>
              <h2>Processing...</h2>
              <p>Placing your order, please wait.</p>
            </>
          )}

          {/* Order queued */}
          {paymentStep === 'queued' && (
            <>
              <h2>Order Queued</h2>
              <p>Your order has been placed and is awaiting payment confirmation.</p>
              <p>You will be redirected to your orders shortly.</p>
            </>
          )}

          {/* Timeout */}
          {paymentStep === 'timeout' && (
            <>
              <h2>Time Out</h2>
              <p>The QR payment window has expired. Please try again.</p>
              <button className="btn-primary" onClick={() => setPaymentStep('idle')}>Back to Cart</button>
            </>
          )}

          {/* Error */}
          {paymentStep === 'error' && (
            <>
              <h2>Something went wrong</h2>
              <p>{paymentError || 'Unable to complete the order.'}</p>
              <button className="btn-primary" onClick={() => setPaymentStep('idle')}>Back to Cart</button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!user) return (
    <div className="page-container cart-auth-wall">
      <button className="back-btn" onClick={() => navigate(-1)}><FiArrowLeft size={20}/></button>
      <div className="auth-wall-content">
        <div className="auth-wall-icon">🛍️</div>
        <h2>Sign in to view your Cart & Orders</h2>
        <p>Keep track of your jewellery collection, orders and more.</p>
        <button className="btn-primary" onClick={() => navigate('/auth')}>Sign In / Sign Up</button>
      </div>
    </div>
  );

  return (
    <div className="page-container">
      <div className="cart-top-head">
        <button className="back-btn" onClick={() => navigate(-1)}><FiArrowLeft size={20}/></button>
        <span className="cart-logo" onClick={() => navigate('/')}>Abhiras</span>
      </div>

      <div className="cart-tabs">
        {TABS.map(t => (
          <button key={t.key} className={`cart-tab${activeTab === t.key ? ' active' : ''}`}
            onClick={() => setActiveTab(t.key)}>
            {t.icon} <span>{t.label}</span>
          </button>
        ))}
      </div>

      <div className="cart-body">
        {loading ? <div className="cart-loading">Loading…</div> : <>

          {/* CART */}
          {activeTab === 'cart' && (
            <div className="cart-section">
              {data.cart.length === 0
                ? <EmptyState icon="🛒" text="Your cart is empty" sub="Add some beautiful jewellery!" />
                : <>
                  <div className="cart-items">
                    {data.cart.slice(0, showMore.cart).map(item => {
                      const productId = item.product_id || item.productid;
                      return (
                        <div key={productId} className="cart-item">
                          <img src={item.image1 ? (item.image1.startsWith('http') ? item.image1 : `/uploads/${item.image1}`) : 'https://placehold.co/80x80/F5F0E8/800020?text=A'} alt={item.product_name} onClick={() => navigate(`/product/${productId}`)} />
                          <div className="cart-item-info">
                            <p className="ci-name" onClick={() => navigate(`/product/${productId}`)}>{item.product_name}</p>
                            <p className="ci-price">₹{Number(item.selling_price).toLocaleString('en-IN')}</p>
                            <div className="ci-qty">
                              <button onClick={() => handleQtyChange(productId, item.quantity - 1)}><FiMinus size={12}/></button>
                              <span>{item.quantity}</span>
                              <button onClick={() => handleQtyChange(productId, item.quantity + 1)}><FiPlus size={12}/></button>
                            </div>
                          </div>
                          <button className="ci-remove" onClick={() => handleRemove(productId, 'C')}><FiTrash2 size={16}/></button>
                        </div>
                      );
                    })}
                    {data.cart.length > showMore.cart && (
                      <button className="show-more-btn" onClick={() => setShowMore(s => ({...s, cart: s.cart + 5}))}>Show More</button>
                    )}
                  </div>
                  <div className="cart-summary">
                    <h3>Order Summary</h3>
                    <div className="summary-row"><span>Subtotal</span><span>₹{Number(cartOrig).toLocaleString('en-IN')}</span></div>
                    {savings > 0 && <div className="summary-row savings"><span>Savings</span><span>−₹{Number(savings).toLocaleString('en-IN')}</span></div>}
                    <div className="summary-row"><span>Shipping</span><span>FREE</span></div>
                    <div className="summary-row total"><span>Total</span><span>₹{Number(cartTotal).toLocaleString('en-IN')}</span></div>
                    <button className="btn-primary checkout-btn" onClick={handleCheckout}>Proceed to Checkout</button>
                  </div>
                </>
              }
            </div>
          )}

          {/* WISHLIST */}
          {activeTab === 'wishlist' && (
            <div className="wishlist-section">
              {data.wishlist.length === 0
                ? <EmptyState icon="💝" text="Your wishlist is empty" sub="Save items you love!" />
                : <div className="wishlist-grid">
                    {data.wishlist.slice(0, showMore.wishlist).map(item => {
                      const productId = item.product_id || item.productid;
                      return (
                        <div key={productId} className="wishlist-card">
                          <img src={item.image1 ? (item.image1.startsWith('http') ? item.image1 : `/uploads/${item.image1}`) : 'https://placehold.co/200x200/F5F0E8/800020?text=A'} alt={item.product_name} onClick={() => navigate(`/product/${productId}`)} />
                          <div className="wl-info">
                            <p className="wl-name">{item.product_name}</p>
                            <p className="wl-price">₹{Number(item.selling_price).toLocaleString('en-IN')}</p>
                            <div className="wl-actions">
                              <button className="btn-primary wl-cart-btn" onClick={async () => { await axios.post('/api/cart', { productId, quantity: 1, flag: 'C' }); toast.success('Moved to cart!'); fetchAll(); fetchCounts(); }}>Add to Cart</button>
                              <button className="wl-remove" onClick={() => handleRemove(productId, 'W')}><FiTrash2 size={14}/></button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
              }
              {data.wishlist.length > showMore.wishlist && (
                <button className="show-more-btn" onClick={() => setShowMore(s => ({...s, wishlist: s.wishlist + 5}))}>Show More</button>
              )}
            </div>
          )}

          {/* CURRENT ORDERS */}
          {activeTab === 'current' && (
            <div className="orders-section">
              {data.current.length === 0
                ? <EmptyState icon="📦" text="No active orders" sub="Your current orders will appear here." />
                : data.current.slice(0, showMore.current).map(order => (
                    <OrderCard key={order.orderid} order={order} showTrack />
                  ))
              }
              {data.current.length > showMore.current && (
                <button className="show-more-btn" onClick={() => setShowMore(s => ({...s, current: s.current + 5}))}>Show More</button>
              )}
            </div>
          )}

          {/* PREVIOUS ORDERS */}
          {activeTab === 'previous' && (
            <div className="orders-section">
              {data.previous.length === 0
                ? <EmptyState icon="🧾" text="No previous orders" sub="Your order history will appear here." />
                : data.previous.slice(0, showMore.previous).map(order => (
                    <OrderCard key={order.orderid} order={order} />
                  ))
              }
              {data.previous.length > showMore.previous && (
                <button className="show-more-btn" onClick={() => setShowMore(s => ({...s, previous: s.previous + 5}))}>Show More</button>
              )}
            </div>
          )}
        </>}
      </div>
    </div>
  );
}

function OrderCard({ order, showTrack }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const statusColor = STATUS_COLORS[order.status] || '#6B6B6B';
  const steps = ['PENDING_CONFIRMATION','CONFIRMED','PACKED','SHIPPED','OUT_FOR_DELIVERY','DELIVERED'];
  const stepIdx = steps.indexOf(order.status);

  return (
    <div className="order-card">
      <div className="order-card-top" onClick={() => setExpanded(e => !e)}>
        <div className="order-left">
          <img src={order.image1 ? (order.image1.startsWith('http') ? order.image1 : `/uploads/${order.image1}`) : 'https://placehold.co/60x60/F5F0E8/800020?text=A'} alt={order.product_name} onClick={e => { e.stopPropagation(); navigate(`/product/${order.productid}`); }} />
          <div>
            <p className="order-name">{order.product_name}</p>
            <p className="order-id">Order #{order.orderid?.toString().slice(-8)}</p>
            <p className="order-date">Placed: {order.placeddatetime ? new Date(order.placeddatetime).toLocaleDateString('en-IN') : '—'}</p>
            {order.expected_delivery && <p className="order-expected">Expected: {new Date(order.expected_delivery).toLocaleDateString('en-IN')}</p>}
          </div>
        </div>
        <div className="order-right">
          <span className="order-status" style={{ color: statusColor, borderColor: statusColor }}>{order.status?.replace(/_/g, ' ')}</span>
          <p className="order-amount">₹{Number(order.transaction_amount || 0).toLocaleString('en-IN')}</p>
        </div>
      </div>
      {expanded && (
        <div className="order-details">
          {showTrack && stepIdx >= 0 && (
            <div className="track-steps">
              {steps.map((s, i) => (
                <div key={s} className={`track-step${i <= stepIdx ? ' done' : ''}`}>
                  <div className="step-dot" />
                  {i < steps.length - 1 && <div className="step-line" />}
                  <span>{s.replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          )}
          {order.deliveryparty && <p className="order-delivery">Carrier: {order.deliveryparty} {order.deliverymode && `(${order.deliverymode})`}</p>}
          {order.link && <a href={order.link} target="_blank" rel="noreferrer" className="track-link">Track on Carrier →</a>}
          {order.paymentmode && <p className="order-payment">Payment: {order.paymentmode}</p>}
          {order.offer_code && Number(order.discount_amount) > 0 && (
            <p className="order-payment">Offer {order.offer_code}: −₹{Number(order.discount_amount).toLocaleString('en-IN')}</p>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ icon, text, sub }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{icon}</div>
      <h3>{text}</h3>
      <p>{sub}</p>
    </div>
  );
}
